import { create } from 'zustand'
import { UserProps } from '@/entities/user'
import { ConversationProps } from '@/entities/conversation'
import { MessageProps } from '@/entities/message'
import { api } from '@/lib/axios'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { toast } from '@/hooks/use-toast'

interface ChatStore {
  inited: boolean
  activeConversation: ConversationProps
  conversations: ConversationProps[]
  error: string | null
  user: UserProps
  setActiveConversation: (ref?: string) => void
  setConversations: (conversation: ConversationProps[]) => void
  sendMessage: (content: string, conversationRef: string, urls?: string[]) => Promise<[MessageProps[], { planBalance: number; balance: number }] | void>
  deleteMessage: (messageRef: string, conversationRef: string) => void
  deleteConversation: (id: string) => Promise<boolean>
  createNewConversation: (message?: string) => Promise<ConversationProps | undefined>
  getConversationByRef: (ref: string) => Promise<ConversationProps | void>
  shareConversation: (ref: string) => string
  stopProcessing: (ref?: string) => Promise<ConversationProps | void>
}

export const useChatStore = create<ChatStore>((set, get) => ({
  inited: false,
  activeConversation: {},
  conversations: [],
  error: null,
  user: {},

  setActiveConversation: (ref?: string) => {
    if (ref) {
      const { conversations } = get()
      set({ activeConversation: conversations.find(c => c.ref == ref) })
    } else {
      set({ activeConversation: undefined })
    }
  },

  setConversations: (conversations: ConversationProps[]) => {
    set({ conversations })
  },

  setUser: (user: UserProps) => {
    set({ user })
  },

  sendMessage: async (content: string, conversationRef: string, paths?: string[]): Promise<[MessageProps[], { planBalance: number; balance: number }] | void> => {
    try {
      const { conversations, user } = get()

      const response = await api.post('/', {
        action: 'message',
        userRef: user?.ref,
        content,
        paths,
        conversationRef
      })

      if (response?.data?.statusCode == HTTP_STATUS_CODE.CREATED) {
        const responseData = response?.data?.body[0]
        const messages = responseData?.messages
        const balanceResponse = responseData?.balanceResponse?.body[0]
        const updatedConversations = conversations.map(conv => {
          if (conv.ref === conversationRef) {
            return {
              ...conv,
              messages,
              updatedAt: new Date(responseData?.updatedAt)
            }
          }
          return conv
        })

        set({ conversations: updatedConversations })

        return [messages, balanceResponse]
      } else {
        if (response?.data?.statusCode == HTTP_STATUS_CODE.GATEWAY_TIMEOUT) {
          window.location.reload()
        } else if (response?.data?.statusCode == HTTP_STATUS_CODE.CONFLICT) {
          setTimeout(() => {
            window.location.reload()
          }, 30000)
        } else if (response?.data?.statusCode == HTTP_STATUS_CODE.FAILED_DEPENDENCY) {
          toast({
            variant: 'destructive',
            title: 'Ops ...',
            description: 'Por favor, continue a conversa em uma nova conversa.'
          })
        } else {
          toast({
            variant: response?.data?.statusCode == HTTP_STATUS_CODE.OK ? 'default' : 'destructive',
            title: response?.data?.statusCode == HTTP_STATUS_CODE.OK ? 'Sucesso!' : 'Ops ...',
            description: response?.data?.statusMessage || `Não foi possível enviar a mensagem no momento. Tente novamente!`
          })
        }
      }
    } catch (err) {
      console.error(err)
      return undefined
    }
  },

  createNewConversation: (message?: string): Promise<ConversationProps | undefined> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { user, conversations } = get()

        const response = await api.post('/', {
          action: 'conversation',
          userRef: user?.ref,
          content: message
        })

        if (response?.data?.statusCode == HTTP_STATUS_CODE.CREATED) {
          const responseData = response?.data?.body[0]

          const newConversation: ConversationProps = {
            id: responseData?.id,
            title: responseData?.title,
            createdAt: new Date(responseData?.createdAt),
            updatedAt: new Date(responseData?.updatedAt),
            messages: [],
            ref: responseData?.ref
          }
          set({ conversations: [newConversation, ...conversations] })
          set({ activeConversation: newConversation })

          resolve(responseData as ConversationProps)
        } else {
          reject(new Error('Não foi possível criar nova conversa'))
        }
      } catch (err) {
        console.error(err)
        return undefined
      }
    })
  },

  shareConversation: (ref: string) => {
    return `${import.meta.env.VITE_BASE_URL}/chat/${ref}`
  },

  getConversationByRef: async (ref: string): Promise<ConversationProps | void> => {
    try {
      const { user, setActiveConversation } = get()

      const response = await api.get('/', {
        params: {
          action: 'conversation-by-ref',
          ref,
          userRef: user?.ref
        }
      })

      const conversation = response.data.body[0]
      setActiveConversation(conversation)
      return conversation
    } catch (err) {
      console.error(err)
    }
  },

  deleteMessage: async (messageRef: string, consersationRef: string) => {
    const { conversations, user } = get()

    await api.delete('/', {
      data: {
        action: 'message',
        ref: messageRef,
        userRef: user?.ref
      }
    })

    const updatedConversations = conversations.map(conv => {
      if (conv.ref === consersationRef) {
        return {
          ...conv,
          messages: conv.messages?.filter(msg => msg.ref !== messageRef)
        }
      }
      return conv
    })

    set({ conversations: updatedConversations })
  },

  deleteConversation: async (ref: string) => {
    try {
      const { conversations, user } = get()

      await api.delete('/', {
        data: {
          action: 'conversation',
          ref,
          userRef: user?.ref
        }
      })

      const updatedConversations = conversations.filter(conv => conv.ref !== ref)
      set({ conversations: updatedConversations })

      return true
    } catch (err) {
      console.error(err)
      return false
    }
  },

  stopProcessing: (ref?: string): Promise<ConversationProps | undefined> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { user } = get()

        const response = await api.post('/', {
          action: 'stop-processing-conversation',
          userRef: user?.ref,
          ref
        })

        if (response?.data?.statusCode == HTTP_STATUS_CODE.OK) {
          resolve(response?.data?.body[0] as ConversationProps)
        } else {
          reject(new Error('Não foi possível parar o processamento da mensagem'))
        }
      } catch (err) {
        console.error(err)
        reject(new Error('Não foi possível parar o processamento da mensagem'))
      }
    })
  }
}))

export const initChatStore = async (user: UserProps) => {
  try {
    const response = await api.get('/', {
      params: {
        action: 'conversation',
        userRef: user?.ref
      }
    })

    const processedConversations = response?.data?.body?.map((conv: ConversationProps) => ({
      ...conv,
      updatedAt: new Date(conv?.updatedAt || ''),
      createdAt: new Date(conv?.createdAt || ''),
      messages: conv?.messages?.map((msg: MessageProps) => ({
        ...msg,
        createdAt: new Date(msg?.createdAt || '')
      }))
    }))
    useChatStore.setState({ conversations: processedConversations, user: user, inited: true })
  } catch (e) {
    console.error('Failed to parse stored conversations', e)
  }
}
